// Port of the orchestration core of Services/SBotChatController.swift:
// wires AxonMultiBarChatState (streaming/agentic loop) to Firestore persistence
// (SBotChatSyncService), personality/effort configuration (aiConfigService),
// and smart titles. iOS additionally keeps a local JSON store; on the web,
// Firestore is the single source of truth.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createMessage, type Message } from "axon-provider-kit/core";
import { useAuth } from "@/auth/AuthProvider";
import {
  DEFAULT_CHAT_EFFORT,
  DEFAULT_CHAT_MODEL_ID,
  chatMessageToProviderMessage,
  createChatConversation,
  providerMessageToChatMessage,
  refreshedTitle,
  toProviderAttachment,
  workspaceScopeFromActiveContext,
  type SBotChatAttachment,
  type SBotChatConversation,
  type SBotChatMessage,
  type SBotChatWorkspaceScope,
  type SBotEffortLevel,
} from "@/models/aiChat";
import {
  buildPersonalizedSystemPrompt,
  APPROVAL_COMPLETION_GUIDANCE,
  defaultWebPersonality,
  isWebAIProviderSupported,
  listenPersonalities,
  normalizeWebConversationSelection,
  resolvedMaxTokens,
  webEnabledPersonalities,
  type SBotAIPersonality,
} from "../services/aiConfigService";
import { SBotChatSyncService } from "../services/chatSyncService";
import { generateSmartTitle } from "../services/titleGenerator";
import { SBotToolApprovalStore } from "../services/toolApprovalStore";
import { SBotToolAuthorizationService } from "../services/toolAuthorization";
import {
  APP_NAVIGATION_TOOL_GUIDANCE,
  SBotToolRegistry,
  ToolName,
} from "../services/toolRegistry";
import { approvalCardFromContent, toolSuccessEnvelope, toolFailureEnvelope } from "../services/toolEnvelope";
import {
  FirestoreSBotAppNavigationResourceChecker,
  SBotAppNavigationAuthorizer,
} from "../services/appNavigationAuthorization";
import { SBotNavigationCapabilityStore } from "../services/navigationCapabilityStore";
import { useAxonChatState } from "./useAxonChatState";
import { toolResultMessage } from "axon-provider-kit/core";
import { ChatTurnContextProjection, type AxonChatStateSnapshot, type AxonMultiBarChatState } from "axon-modal-chat-kit/axon-adapter";
import {
  chatToolUserInputRequestFromJSON,
  createCancelledChatToolUserInputResponse,
  createChatToolUserInputDraft,
  validateChatToolUserInputResponse,
  type ChatToolUserInputDraft,
  type ChatToolUserInputRequest,
  type ChatToolUserInputResponse,
} from "axon-modal-chat-kit/core";
import { PROXY_API_KEY_PLACEHOLDER } from "../services/proxyTransport";
import { fetchAIUsageSummary } from "@/services/aiUsageLimits";
import type { AIUsageSummary } from "@/models/aiUsageLimits";
import {
  AIUsageGate,
  aiUsageScopeKey,
  type AIUsageGateScope,
} from "../services/aiUsageGate";
import type {
  SBotAppEnvironmentContextProviding,
  SBotAppEnvironmentInteraction,
} from "../services/appEnvironmentContext";

export interface AIChatController {
  /** The underlying chat engine — exposed so widget hosts can subscribe directly. */
  state: AxonMultiBarChatState;
  snapshot: AxonChatStateSnapshot;
  conversation: SBotChatConversation;
  recents: SBotChatConversation[];
  legacyRecents: SBotChatConversation[];
  personalities: SBotAIPersonality[];
  selectedPersonality: SBotAIPersonality;
  selectedEffort: SBotEffortLevel;
  isLoadingConversation: boolean;
  pendingUserInputRequest: ChatToolUserInputRequest | null;
  userInputDraft: ChatToolUserInputDraft | null;
  userInputError: string | null;
  isAwaitingUserInput: boolean;
  isSubmittingUserInput: boolean;
  isExecutingApprovedAction: boolean;
  approvalContinuationError: string | null;
  usageSummary: AIUsageSummary | null;
  usageLimitBlockMessage: string | null;
  refreshUsageSummary: (force?: boolean) => Promise<AIUsageSummary | null>;
  send: (text: string, attachments?: SBotChatAttachment[]) => Promise<boolean>;
  stop: () => void;
  steer: (text: string) => Promise<boolean>;
  startNewChat: () => void;
  openConversation: (conversationID: string) => Promise<void>;
  deleteConversation: (conversationID: string) => void;
  setPersonalityID: (personalityID: string) => void;
  setEffort: (effort: SBotEffortLevel) => void;
  updateUserInputDraft: (draft: ChatToolUserInputDraft) => void;
  submitPendingUserInput: (response: ChatToolUserInputResponse) => void;
  cancelPendingUserInput: () => void;
  approveToolAction: (approvalID: string) => Promise<void>;
  rejectToolAction: (approvalID: string) => void;
  canDecideToolApproval: (approvalID: string) => boolean;
  /** Live approval status lookup for rendering (records are in-memory, per session). */
  approvalStatus: (approvalID: string) => string | undefined;
}

export interface AIChatNavigationHost {
  openAppPath: (appPath: string) => Promise<boolean>;
  origin: () => string;
}

interface PendingUserInputResolutionSnapshot {
  conversation: SBotChatConversation;
  providerMessages: Message[];
  knownMessages: Map<string, SBotChatMessage>;
  request: ChatToolUserInputRequest;
  nextToolRoundCount: number;
  draft: ChatToolUserInputDraft;
  startedStreaming: boolean;
}

export function useAIChatController(
  navigationHost?: AIChatNavigationHost,
  appEnvironmentContext?: SBotAppEnvironmentContextProviding,
): AIChatController {
  const { currentUser, isSBotLabsAdmin, workspaceOptions } = useAuth();
  const { state, snapshot } = useAxonChatState();
  const syncService = useMemo(() => new SBotChatSyncService(), []);
  const approvalStore = useMemo(() => new SBotToolApprovalStore(), []);
  const navigationCapabilityStore = useMemo(() => new SBotNavigationCapabilityStore(), []);
  const workspaceNavigationIndex = useMemo(() => new FirestoreSBotAppNavigationResourceChecker(), []);
  const appNavigationAuthorizer = useMemo(
    () => new SBotAppNavigationAuthorizer(workspaceNavigationIndex),
    [workspaceNavigationIndex],
  );
  const aiUsageGate = useMemo(
    () => new AIUsageGate<AIUsageSummary>((request) => fetchAIUsageSummary(request)),
    [],
  );

  const userID = currentUser?.id;
  const workspaceScope: SBotChatWorkspaceScope | undefined = useMemo(
    () => (currentUser?.activeContext ? workspaceScopeFromActiveContext(currentUser.activeContext) : undefined),
    [currentUser?.activeContext],
  );
  const aiUsageScope: AIUsageGateScope | null = useMemo(() => {
    if (!userID) return null;
    return workspaceScope?.leagueID
      ? { kind: "league", leagueID: workspaceScope.leagueID }
      : { kind: "personal", userID };
  }, [userID, workspaceScope?.leagueID]);
  const aiUsageScopeIdentity = aiUsageScope ? aiUsageScopeKey(aiUsageScope) : null;
  const aiUsageScopeIdentityRef = useRef(aiUsageScopeIdentity);
  aiUsageScopeIdentityRef.current = aiUsageScopeIdentity;

  const [personalities, setPersonalities] = useState<SBotAIPersonality[]>([]);
  const [selectedPersonalityID, setSelectedPersonalityID] = useState<string | undefined>(undefined);
  const [selectedEffort, setSelectedEffortState] = useState<SBotEffortLevel>(DEFAULT_CHAT_EFFORT);
  const [recents, setRecents] = useState<SBotChatConversation[]>([]);
  const [legacyRecents, setLegacyRecents] = useState<SBotChatConversation[]>([]);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [usageSummary, setUsageSummary] = useState<AIUsageSummary | null>(null);
  const [usageLimitBlockMessage, setUsageLimitBlockMessage] = useState<string | null>(null);
  const [pendingUserInputRequest, setPendingUserInputRequest] = useState<ChatToolUserInputRequest | null>(null);
  const [userInputDraft, setUserInputDraft] = useState<ChatToolUserInputDraft | null>(null);
  const [userInputError, setUserInputError] = useState<string | null>(null);
  const [isExecutingApprovedAction, setIsExecutingApprovedAction] = useState(false);
  const [approvalContinuationError, setApprovalContinuationError] = useState<string | null>(null);
  const contextProjectionRef = useRef(new ChatTurnContextProjection());
  const baseSystemPromptRef = useRef("You are a helpful assistant.");
  const activeEnvironmentInteractionRef = useRef<{
    interaction: SBotAppEnvironmentInteraction;
  } | null>(null);

  const availablePersonalities = useMemo(
    () => webEnabledPersonalities(personalities),
    [personalities],
  );
  const selectedPersonality = useMemo(() => {
    return availablePersonalities.find((personality) => personality.id === selectedPersonalityID)
      ?? defaultWebPersonality(personalities);
  }, [availablePersonalities, personalities, selectedPersonalityID]);

  // Current conversation lives in a ref (mutated on every streaming tick) with
  // a state mirror for rendering metadata (title etc.).
  const conversationRef = useRef<SBotChatConversation>(
    createChatConversation({ selectedModelID: DEFAULT_CHAT_MODEL_ID, workspaceScope }),
  );
  const [conversationVersion, setConversationVersion] = useState(0);
  const bumpConversation = useCallback(() => setConversationVersion((value) => value + 1), []);

  /** Known message metadata by id, so Firestore-loaded fields survive provider round-trips. */
  const knownMessagesRef = useRef(new Map<string, SBotChatMessage>());
  const wasStreamingRef = useRef(false);
  const usageWasStreamingRef = useRef(false);
  const titleTasksRef = useRef(new Set<string>());
  const approvalHandoffRunRef = useRef<{ existingMessageIDs: Set<string> } | null>(null);
  const pendingUserInputConversationIDRef = useRef<string | null>(null);
  const pendingUserInputIdentityRef = useRef<string | null>(null);
  const pendingUserInputResolutionRef = useRef<PendingUserInputResolutionSnapshot | null>(null);
  const approvalExecutionRef = useRef<{ conversationID: string; cancelled: boolean } | null>(null);
  // Keep completed evidence recoverable during this session even if a detached
  // conversation cannot be saved (for example, a sign-out during execution).
  const unsavedApprovalConversationsRef = useRef(new Map<string, SBotChatConversation>());
  const conversationLoadGenerationRef = useRef(0);
  const sessionIdentityRef = useRef({ userID, workspaceKey: workspaceScope?.storageKey });
  sessionIdentityRef.current = { userID, workspaceKey: workspaceScope?.storageKey };
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (approvalExecutionRef.current) approvalExecutionRef.current.cancelled = true;
    };
  }, []);

  const isAwaitingUserInput = pendingUserInputRequest !== null;
  const isSubmittingUserInput = userInputDraft?.isSubmitting === true;

  // --- Personalities ---
  useEffect(() => listenPersonalities(setPersonalities), []);

  // --- League tool registry (SBotToolRegistry port) ---
  const toolRegistry = useMemo(() => {
    if (!currentUser) return null;
    const authorization = new SBotToolAuthorizationService({
      currentUser,
      workspaceOptions: workspaceOptions.map((option) => ({
        leagueID: option.leagueID,
        sportID: option.sportID,
        displayName: option.displayTitle,
      })),
      workspaceScope,
      isSBotLabsAdmin,
    });
    return new SBotToolRegistry({
      authorization,
      approvalStore,
      ...(appEnvironmentContext ? { appEnvironmentContext } : {}),
      ...(navigationHost
        ? {
            navigation: {
              capabilityStore: navigationCapabilityStore,
              index: workspaceNavigationIndex,
              authorizer: appNavigationAuthorizer,
              currentSession: () => ({
                userID: currentUser.id,
                conversationID: conversationRef.current.id,
                workspaceStorageKey: workspaceScope?.storageKey,
              }),
              origin: navigationHost.origin,
              open: navigationHost.openAppPath,
            },
          }
        : {}),
    });
  }, [
    currentUser,
    workspaceOptions,
    workspaceScope,
    isSBotLabsAdmin,
    approvalStore,
    navigationHost,
    navigationCapabilityStore,
    workspaceNavigationIndex,
    appNavigationAuthorizer,
    appEnvironmentContext,
  ]);

  useEffect(() => {
    state.configureTools(toolRegistry);
  }, [state, toolRegistry]);

  // --- Recents listeners (workspace + legacy personal archive) ---
  useEffect(() => {
    if (!userID) return;
    const unsubscribe = syncService.listenConversationSummaries(userID, workspaceScope, setRecents);
    let unsubscribeLegacy: (() => void) | undefined;
    if (workspaceScope) {
      unsubscribeLegacy = syncService.listenConversationSummaries(userID, undefined, setLegacyRecents);
    } else {
      setLegacyRecents([]);
    }
    return () => {
      unsubscribe();
      unsubscribeLegacy?.();
    };
  }, [syncService, userID, workspaceScope]);

  // --- Provider configuration (personality + effort + personalization) ---
  useEffect(() => {
    const currentConversation = conversationRef.current;
    const conversation = normalizeWebConversationSelection(personalities, currentConversation);
    const modelID = conversation.messages.length > 0 ? conversation.selectedModelID : selectedPersonality.modelID;
    if (
      conversation !== currentConversation
      || conversation.selectedModelID !== modelID
      || conversation.selectedPersonalityID !== selectedPersonality.id
      || conversation.selectedEffort !== selectedEffort
    ) {
      conversationRef.current = {
        ...conversation,
        selectedModelID: modelID,
        selectedPersonalityID: selectedPersonality.id,
        selectedEffort,
      };
      bumpConversation();
    }
    const personalizedPrompt = `${buildPersonalizedSystemPrompt(selectedPersonality, currentUser)}${APPROVAL_COMPLETION_GUIDANCE}`;
    const baseSystemPrompt = navigationHost
      ? `${personalizedPrompt}${APP_NAVIGATION_TOOL_GUIDANCE}`
      : personalizedPrompt;
    baseSystemPromptRef.current = baseSystemPrompt;
    state.systemPrompt = baseSystemPrompt;
    state.promptCacheKey = conversationRef.current.id;
    state.maxTokens = resolvedMaxTokens(selectedPersonality);
    if (selectedPersonality.modelSettings.maxToolRounds !== undefined) {
      state.maxToolRounds = selectedPersonality.modelSettings.maxToolRounds;
    }
    // iOS parity: SBotAxonMultiBarChatRunner configures provider/model/key only —
    // model settings (incl. reasoning effort) are NOT sent in the chat streaming
    // path. OpenAI also rejects tools + reasoning_effort on /chat/completions.
    // selectedEffort still persists on the conversation doc like iOS.
    // selectedPersonality is guaranteed by webEnabledPersonalities to use one
    // of the providers accepted by the web proxy.
    if (!isWebAIProviderSupported(selectedPersonality.providerID)) return;
    state.configure({
      provider: selectedPersonality.providerID,
      model: modelID,
      apiKey: PROXY_API_KEY_PLACEHOLDER,
    });
  }, [state, personalities, selectedPersonality, selectedEffort, currentUser, bumpConversation, conversationVersion, navigationHost]);

  const endEnvironmentInteraction = useCallback(() => {
    const active = activeEnvironmentInteractionRef.current;
    if (!active) return;
    appEnvironmentContext?.endInteraction(active.interaction);
    activeEnvironmentInteractionRef.current = null;
    state.systemPrompt = baseSystemPromptRef.current;
  }, [appEnvironmentContext, state]);

  const resetRequestContext = useCallback(() => {
    contextProjectionRef.current = new ChatTurnContextProjection();
    state.requestPreflight = undefined;
    state.promptCacheKey = undefined;
  }, [state]);

  const configureRequestContext = useCallback((interaction: SBotAppEnvironmentInteraction | undefined) => {
    const projection = contextProjectionRef.current;
    const priorMessageIDs = new Set(state.getSnapshot().messages.map((message) => message.id));
    const context = interaction?.compactInstructions;
    state.promptCacheKey = conversationRef.current.id;
    state.systemPrompt = baseSystemPromptRef.current;
    state.requestPreflight = (messages) => projection.project(messages, context, priorMessageIDs);
    if (interaction) activeEnvironmentInteractionRef.current = { interaction };
  }, [state]);

  const requestCleanupRef = useRef(() => {});
  requestCleanupRef.current = () => {
    state.stop();
    endEnvironmentInteraction();
    resetRequestContext();
  };
  useEffect(() => () => requestCleanupRef.current(), []);

  const clearPendingUserInputState = useCallback(() => {
    pendingUserInputIdentityRef.current = null;
    pendingUserInputConversationIDRef.current = null;
    setPendingUserInputRequest(null);
    setUserInputDraft(null);
    setUserInputError(null);
  }, []);

  const installPendingUserInput = useCallback(
    (request: ChatToolUserInputRequest, conversationID = conversationRef.current.id) => {
      const identity = `${conversationID}\u0000${request.id}`;
      const requestChanged = pendingUserInputIdentityRef.current !== identity;
      pendingUserInputIdentityRef.current = identity;
      pendingUserInputConversationIDRef.current = conversationID;
      setPendingUserInputRequest(request);
      if (requestChanged) {
        setUserInputDraft(createChatToolUserInputDraft(request.id));
        setUserInputError(null);
      }
    },
    [],
  );

  const rollbackPendingUserInputResolution = useCallback(
    (reason?: string) => {
      const resolution = pendingUserInputResolutionRef.current;
      if (!resolution) return false;

      pendingUserInputResolutionRef.current = null;
      approvalHandoffRunRef.current = null;
      state.stop();
      knownMessagesRef.current = new Map(resolution.knownMessages);
      conversationRef.current = {
        ...resolution.conversation,
        messages: [...resolution.conversation.messages],
      };
      state.restoreMessages(resolution.providerMessages);
      try {
        state.restorePendingUserInput(resolution.request, resolution.nextToolRoundCount);
      } catch (error) {
        console.warn("Could not restore pending clarification:", error);
        clearPendingUserInputState();
        bumpConversation();
        return true;
      }

      pendingUserInputIdentityRef.current = `${resolution.conversation.id}\u0000${resolution.request.id}`;
      pendingUserInputConversationIDRef.current = resolution.conversation.id;
      setPendingUserInputRequest(resolution.request);
      setUserInputDraft({
        ...resolution.draft,
        isSubmitting: false,
        answersByQuestionId: { ...resolution.draft.answersByQuestionId },
      });
      setUserInputError(reason ?? null);
      bumpConversation();
      if (userID) {
        syncService.flushPendingSave(conversationRef.current, userID, workspaceScope);
      }
      return true;
    },
    [bumpConversation, clearPendingUserInputState, state, syncService, userID, workspaceScope],
  );

  // --- Reset when the workspace changes ---
  useEffect(() => {
    if (approvalExecutionRef.current) approvalExecutionRef.current.cancelled = true;
    conversationLoadGenerationRef.current += 1;
    setIsLoadingConversation(false);
    setApprovalContinuationError(null);
    approvalHandoffRunRef.current = null;
    pendingUserInputResolutionRef.current = null;
    clearPendingUserInputState();
    endEnvironmentInteraction();
    state.stop();
    state.clearHistory();
    resetRequestContext();
    knownMessagesRef.current.clear();
    conversationRef.current = createChatConversation({
      selectedModelID: selectedPersonality.modelID,
      selectedPersonalityID: selectedPersonality.id,
      selectedEffort,
      workspaceScope,
    });
    bumpConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, workspaceScope?.storageKey]);

  const refreshUsageSummary = useCallback(async (force = false): Promise<AIUsageSummary | null> => {
    if (!aiUsageScope) {
      setUsageSummary(null);
      setUsageLimitBlockMessage(null);
      return null;
    }
    const result = await aiUsageGate.refresh(aiUsageScope, { force });
    if (aiUsageScopeIdentityRef.current !== aiUsageScopeKey(aiUsageScope)) return null;
    setUsageSummary(result.summary);
    setUsageLimitBlockMessage(result.blockMessage);
    return result.summary;
  }, [aiUsageGate, aiUsageScope]);

  // Scope changes must not reuse a previous user's or workspace's decision.
  useEffect(() => {
    aiUsageGate.invalidate();
    setUsageSummary(null);
    setUsageLimitBlockMessage(null);
    void refreshUsageSummary(true);
  }, [aiUsageGate, refreshUsageSummary]);

  // A response that was allowed to begin always finishes. Refresh only after
  // the stream ends so the next request sees the latest server-owned usage.
  useEffect(() => {
    const wasStreaming = usageWasStreamingRef.current;
    usageWasStreamingRef.current = snapshot.isStreaming;
    if (wasStreaming && !snapshot.isStreaming) void refreshUsageSummary(true);
  }, [snapshot.isStreaming, refreshUsageSummary]);

  useEffect(() => {
    if (!snapshot.isStreaming) endEnvironmentInteraction();
  }, [snapshot.isStreaming, endEnvironmentInteraction]);

  const activeConversationID = conversationRef.current.id;
  useEffect(() => {
    if (!userID) {
      navigationCapabilityStore.clearSession();
      return;
    }
    navigationCapabilityStore.beginSession({
      userID,
      conversationID: activeConversationID,
      workspaceStorageKey: workspaceScope?.storageKey,
    });
  }, [
    navigationCapabilityStore,
    userID,
    workspaceScope?.storageKey,
    activeConversationID,
  ]);

  // --- Mirror provider messages into the conversation + persist ---
  useEffect(() => {
    const conversation = conversationRef.current;
    let mapped = snapshot.messages
      .filter((message) => message.role !== "system")
      .map((message) => toChatMessage(message, knownMessagesRef.current, conversation, selectedPersonality));
    const completedRun = wasStreamingRef.current && !snapshot.isStreaming
      ? approvalHandoffRunRef.current
      : null;
    if (completedRun) {
      approvalHandoffRunRef.current = null;
      if (snapshot.error == null) {
        mapped = completeApprovalHandoff(mapped, completedRun.existingMessageIDs);
        for (const message of mapped) knownMessagesRef.current.set(message.id, message);
      }
    }
    const changed = mapped.length !== conversation.messages.length || snapshot.isStreaming !== wasStreamingRef.current;

    if (mapped.length > 0 && changed) {
      const next: SBotChatConversation = { ...conversation, messages: mapped, updatedAt: new Date() };
      next.title = refreshedTitle(next);
      conversationRef.current = next;
      bumpConversation();

      if (userID) {
        const recoveryKey = approvalRecoveryKey(userID, workspaceScope, next.id);
        if (unsavedApprovalConversationsRef.current.has(recoveryKey)) {
          unsavedApprovalConversationsRef.current.set(recoveryKey, next);
        }
        if (snapshot.isStreaming) {
          // Deferred while streaming (mirrors iOS).
        } else if (wasStreamingRef.current) {
          syncService.flushPendingSave(next, userID, workspaceScope);
          void maybeGenerateSmartTitle(next);
        } else {
          syncService.saveConversationDebounced(next, userID, workspaceScope);
        }
      }
    }
    wasStreamingRef.current = snapshot.isStreaming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.messages, snapshot.isStreaming]);

  // The Axon state owns the live suspension. The controller owns the draft and
  // keeps it available across the dedicated/floating presentation handoff.
  useEffect(() => {
    if (pendingUserInputResolutionRef.current) return;
    if (snapshot.pendingUserInput) {
      installPendingUserInput(snapshot.pendingUserInput);
    } else if (!snapshot.isStreaming) {
      clearPendingUserInputState();
    }
  }, [
    clearPendingUserInputState,
    installPendingUserInput,
    snapshot.isStreaming,
    snapshot.pendingUserInput,
  ]);

  // Submission removes pendingUserInput synchronously and starts a new stream.
  // Wait until that stream has actually been observed before treating idle as
  // terminal, otherwise a local draft-state render could finalize too early.
  useEffect(() => {
    const resolution = pendingUserInputResolutionRef.current;
    if (!resolution?.startedStreaming || snapshot.isStreaming) return;

    if (snapshot.error != null) {
      rollbackPendingUserInputResolution(describeError(snapshot.error));
      return;
    }

    pendingUserInputResolutionRef.current = null;
    setUserInputError(null);
    if (snapshot.pendingUserInput) {
      installPendingUserInput(snapshot.pendingUserInput);
    } else {
      clearPendingUserInputState();
    }
  }, [
    clearPendingUserInputState,
    installPendingUserInput,
    rollbackPendingUserInputResolution,
    snapshot.error,
    snapshot.isStreaming,
    snapshot.pendingUserInput,
  ]);

  const maybeGenerateSmartTitle = useCallback(
    async (conversation: SBotChatConversation) => {
      if (!userID) return;
      if (conversation.smartTitleGeneratedAt) return;
      if (titleTasksRef.current.has(conversation.id)) return;
      const userMessage = conversation.messages.find((message) => message.role === "user");
      const assistantMessage = conversation.messages.find((message) => message.role === "assistant" && message.content.trim().length > 0);
      if (!userMessage || !assistantMessage) return;

      // One attempt per conversation per session — failures don't retry-loop.
      titleTasksRef.current.add(conversation.id);
      try {
        const title = await generateSmartTitle(userMessage, assistantMessage);
        if (!title) return;
        if (conversationRef.current.id === conversation.id) {
          conversationRef.current = { ...conversationRef.current, title, smartTitleGeneratedAt: new Date(), updatedAt: new Date() };
          bumpConversation();
          syncService.flushPendingSave(conversationRef.current, userID, workspaceScope);
        }
      } catch (error) {
        console.warn("Smart title generation failed:", error);
      }
    },
    [bumpConversation, syncService, userID, workspaceScope],
  );

  // --- Actions ---

  const updateUserInputDraft = useCallback(
    (draft: ChatToolUserInputDraft) => {
      if (
        !pendingUserInputRequest
        || pendingUserInputConversationIDRef.current !== conversationRef.current.id
        || draft.requestId !== pendingUserInputRequest.id
        || userInputDraft?.isSubmitting
      ) {
        return;
      }
      setUserInputDraft({
        ...draft,
        answersByQuestionId: { ...draft.answersByQuestionId },
      });
      setUserInputError(null);
    },
    [pendingUserInputRequest, userInputDraft?.isSubmitting],
  );

  const beginPendingUserInputResolution = useCallback(
    (response: ChatToolUserInputResponse) => {
      const request = pendingUserInputRequest;
      const draft = userInputDraft;
      if (
        !request
        || !draft
        || draft.isSubmitting
        || isLoadingConversation
        || state.getSnapshot().isStreaming
        || pendingUserInputResolutionRef.current
        || pendingUserInputConversationIDRef.current !== conversationRef.current.id
      ) {
        return;
      }

      try {
        validateChatToolUserInputResponse(response, request);
      } catch (error) {
        setUserInputError(describeError(error));
        return;
      }

      const providerMessages = [...state.getSnapshot().messages];
      const reconstructed = pendingUserInputFromProviderMessages(providerMessages);
      if (!reconstructed || reconstructed.request.id !== request.id) {
        setUserInputError("The clarification no longer matches this conversation. Reopen the chat and try again.");
        return;
      }

      const conversation = conversationRef.current;
      const resolution: PendingUserInputResolutionSnapshot = {
        conversation: {
          ...conversation,
          messages: [...conversation.messages],
        },
        providerMessages,
        knownMessages: new Map(knownMessagesRef.current),
        request,
        nextToolRoundCount: reconstructed.nextToolRoundCount,
        draft: {
          ...draft,
          isSubmitting: false,
          answersByQuestionId: { ...draft.answersByQuestionId },
        },
        startedStreaming: false,
      };
      pendingUserInputResolutionRef.current = resolution;
      setUserInputDraft({
        ...draft,
        isSubmitting: true,
        answersByQuestionId: { ...draft.answersByQuestionId },
      });
      setUserInputError(null);

      endEnvironmentInteraction();
      const interaction = appEnvironmentContext?.beginInteraction("clarificationContinuation");
      configureRequestContext(interaction);

      approvalHandoffRunRef.current = {
        existingMessageIDs: new Set(conversation.messages.map((message) => message.id)),
      };
      try {
        state.resolvePendingUserInput(response);
        resolution.startedStreaming = true;
      } catch (error) {
        rollbackPendingUserInputResolution(describeError(error));
        endEnvironmentInteraction();
      }
    },
    [
      appEnvironmentContext,
      configureRequestContext,
      endEnvironmentInteraction,
      isLoadingConversation,
      pendingUserInputRequest,
      rollbackPendingUserInputResolution,
      state,
      userInputDraft,
    ],
  );

  const submitPendingUserInput = useCallback(
    (response: ChatToolUserInputResponse) => beginPendingUserInputResolution(response),
    [beginPendingUserInputResolution],
  );

  const cancelPendingUserInput = useCallback(() => {
    const request = pendingUserInputRequest;
    if (!request) return;
    beginPendingUserInputResolution(createCancelledChatToolUserInputResponse(request.id));
  }, [beginPendingUserInputResolution, pendingUserInputRequest]);

  const send = useCallback(
    async (text: string, attachments: SBotChatAttachment[] = []): Promise<boolean> => {
      if (text.trim().length === 0 && attachments.length === 0) return false;
      if (state.getSnapshot().isStreaming || pendingUserInputRequest || approvalExecutionRef.current) return false;
      const originConversationID = conversationRef.current.id;
      const originUserID = sessionIdentityRef.current.userID;
      if (aiUsageScope) {
        const gate = await aiUsageGate.refresh(aiUsageScope);
        if (aiUsageScopeIdentityRef.current !== aiUsageScopeKey(aiUsageScope)) return false;
        setUsageSummary(gate.summary);
        setUsageLimitBlockMessage(gate.blockMessage);
        if (gate.blockMessage) return false;
      }
      if (approvalExecutionRef.current || state.getSnapshot().isStreaming || pendingUserInputRequest) return false;
      if (conversationRef.current.id !== originConversationID || sessionIdentityRef.current.userID !== originUserID) return false;
      endEnvironmentInteraction();
      setApprovalContinuationError(null);
      const interaction = appEnvironmentContext?.beginInteraction("text");
      configureRequestContext(interaction);
      try {
        approvalHandoffRunRef.current = {
          existingMessageIDs: new Set(conversationRef.current.messages.map((message) => message.id)),
        };
        state.send(text, attachments.map(toProviderAttachment));
        return true;
      } catch (error) {
        approvalHandoffRunRef.current = null;
        endEnvironmentInteraction();
        throw error;
      }
    },
    [state, aiUsageGate, aiUsageScope, appEnvironmentContext, configureRequestContext, endEnvironmentInteraction, pendingUserInputRequest],
  );

  const stop = useCallback(() => {
    conversationLoadGenerationRef.current += 1;
    setIsLoadingConversation(false);
    if (approvalExecutionRef.current) approvalExecutionRef.current.cancelled = true;
    approvalHandoffRunRef.current = null;
    if (rollbackPendingUserInputResolution()) {
      endEnvironmentInteraction();
      return;
    }
    state.stop();
    endEnvironmentInteraction();
  }, [state, endEnvironmentInteraction, rollbackPendingUserInputResolution]);

  const steer = useCallback(
    async (text: string): Promise<boolean> => {
      if (text.trim().length === 0) return false;
      if (pendingUserInputRequest || approvalExecutionRef.current) return false;
      const originConversationID = conversationRef.current.id;
      const originUserID = sessionIdentityRef.current.userID;
      if (aiUsageScope) {
        const gate = await aiUsageGate.refresh(aiUsageScope);
        if (aiUsageScopeIdentityRef.current !== aiUsageScopeKey(aiUsageScope)) return false;
        setUsageSummary(gate.summary);
        setUsageLimitBlockMessage(gate.blockMessage);
        if (gate.blockMessage) return false;
      }
      if (approvalExecutionRef.current || pendingUserInputRequest) return false;
      if (conversationRef.current.id !== originConversationID || sessionIdentityRef.current.userID !== originUserID) return false;
      approvalHandoffRunRef.current = null;
      state.interrupt();
      endEnvironmentInteraction();
      const interaction = appEnvironmentContext?.beginInteraction("text");
      configureRequestContext(interaction);
      try {
        approvalHandoffRunRef.current = {
          existingMessageIDs: new Set(conversationRef.current.messages.map((message) => message.id)),
        };
        state.send(text);
        return true;
      } catch (error) {
        approvalHandoffRunRef.current = null;
        endEnvironmentInteraction();
        throw error;
      }
    },
    [state, aiUsageGate, aiUsageScope, appEnvironmentContext, configureRequestContext, endEnvironmentInteraction, pendingUserInputRequest],
  );

  const startNewChat = useCallback(() => {
    stop();
    state.clearHistory();
    resetRequestContext();
    clearPendingUserInputState();
    knownMessagesRef.current.clear();
    conversationRef.current = createChatConversation({
      selectedModelID: selectedPersonality.modelID,
      selectedPersonalityID: selectedPersonality.id,
      selectedEffort,
      workspaceScope,
    });
    bumpConversation();
  }, [state, stop, resetRequestContext, clearPendingUserInputState, selectedPersonality, selectedEffort, workspaceScope, bumpConversation]);

  const openConversation = useCallback(
    async (conversationID: string) => {
      if (!userID) return;
      const recoveryKey = approvalRecoveryKey(userID, workspaceScope, conversationID);
      const retained = unsavedApprovalConversationsRef.current.get(recoveryKey);
      const summary = recents.find((conversation) => conversation.id === conversationID) ?? retained;
      if (!summary) return;

      stop();
      const generation = ++conversationLoadGenerationRef.current;
      const isCurrentLoad = () => mountedRef.current
        && conversationLoadGenerationRef.current === generation
        && sessionIdentityRef.current.userID === userID
        && sessionIdentityRef.current.workspaceKey === workspaceScope?.storageKey;
      setIsLoadingConversation(true);
      try {
        let messages: SBotChatMessage[];
        try {
          messages = await syncService.loadConversationMessages(userID, workspaceScope, conversationID);
        } catch (error) {
          if (!retained) throw error;
          messages = [];
        }
        if (!isCurrentLoad()) return;
        if (retained) {
          const byID = new Map(messages.map((message) => [message.id, message]));
          for (const message of retained.messages) byID.set(message.id, message);
          messages = [...byID.values()].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
        }
        const restoredConversation: SBotChatConversation = { ...summary, messages };
        const conversation = normalizeWebConversationSelection(personalities, restoredConversation);
        knownMessagesRef.current = new Map(messages.map((message) => [message.id, message]));
        conversationRef.current = conversation;
        if (conversation.selectedPersonalityID) setSelectedPersonalityID(conversation.selectedPersonalityID);
        setSelectedEffortState(conversation.selectedEffort);
        const providerMessages = messages.map(chatMessageToProviderMessage);
        resetRequestContext();
        state.restoreMessages(providerMessages);
        const pending = pendingUserInputFromProviderMessages(providerMessages);
        if (pending) {
          try {
            state.restorePendingUserInput(pending.request, pending.nextToolRoundCount);
            installPendingUserInput(pending.request, conversation.id);
          } catch (error) {
            console.warn("Ignoring invalid persisted clarification:", error);
            clearPendingUserInputState();
          }
        } else {
          clearPendingUserInputState();
        }
        bumpConversation();
        if (retained) {
          try {
            await syncService.flushPendingSaveAsync(conversation, userID, workspaceScope);
            if (unsavedApprovalConversationsRef.current.get(recoveryKey) === retained) {
              unsavedApprovalConversationsRef.current.delete(recoveryKey);
            }
            if (isCurrentLoad()) setApprovalContinuationError(null);
          } catch (error) {
            if (isCurrentLoad()) setApprovalContinuationError(`The execution result is retained in this session but could not be saved: ${describeError(error)}.`);
          }
        }
      } catch (error: unknown) {
        // Attachment reads already degrade to empty bytes, so this is a backstop:
        // a rejection here must not escape as an unhandled promise rejection.
        console.warn(`Failed to open conversation ${conversationID}:`, error);
      } finally {
        if (isCurrentLoad()) setIsLoadingConversation(false);
      }
    },
    [
      userID,
      workspaceScope,
      recents,
      state,
      stop,
      syncService,
      resetRequestContext,
      personalities,
      installPendingUserInput,
      clearPendingUserInputState,
      bumpConversation,
    ],
  );

  const deleteConversation = useCallback(
    (conversationID: string) => {
      if (approvalExecutionRef.current?.conversationID === conversationID) return;
      if (!userID) return;
      const recoveryKey = approvalRecoveryKey(userID, workspaceScope, conversationID);
      const target = recents.find((conversation) => conversation.id === conversationID) ?? unsavedApprovalConversationsRef.current.get(recoveryKey);
      unsavedApprovalConversationsRef.current.delete(recoveryKey);
      bumpConversation();
      if (target) void syncService.softDeleteConversation(target, userID, workspaceScope);
      if (conversationRef.current.id === conversationID) startNewChat();
    },
    [userID, workspaceScope, recents, syncService, startNewChat, bumpConversation],
  );

  const canDecideToolApproval = useCallback(
    (approvalID: string): boolean => {
      if (!currentUser || state.getSnapshot().isStreaming || approvalExecutionRef.current || isLoadingConversation || pendingUserInputRequest) return false;
      const conversation = conversationRef.current;
      if (conversation.workspaceScope?.storageKey !== workspaceScope?.storageKey) return false;

      const approvalIndex = lastIndexWhere(
        conversation.messages,
        (message) => approvalCardFromContent(message.content)?.approvalID === approvalID,
      );
      if (approvalIndex < 0) return false;
      const approvalMessage = conversation.messages[approvalIndex];
      const card = approvalMessage ? approvalCardFromContent(approvalMessage.content) : undefined;
      const anchorID = approvalMessage?.approvalPresentationAfterMessageID;
      if (approvalMessage?.role !== "tool" || card?.status !== "pending" || !anchorID) return false;

      const anchorIndex = conversation.messages.findIndex(
        (message) =>
          message.id === anchorID
          && message.role === "assistant"
          && message.toolCalls.length === 0
          && message.content.trim().length > 0,
      );
      if (anchorIndex <= approvalIndex) return false;

      const record = approvalStore.record(approvalID);
      return record?.userID === currentUser.id
        && record.status === "pending"
        && record.expiresAt.getTime() > Date.now();
    },
    [
      approvalStore,
      currentUser,
      isLoadingConversation,
      pendingUserInputRequest,
      snapshot.isStreaming,
      state,
      workspaceScope?.storageKey,
    ],
  );

  // Persist real execution evidence before resuming the model. The matching
  // assistant call is essential: provider history drops orphan tool results.
  const approveToolAction = useCallback(
    async (approvalID: string) => {
      if (!currentUser || !toolRegistry || !canDecideToolApproval(approvalID)) return;
      const execution = { conversationID: conversationRef.current.id, cancelled: false };
      approvalExecutionRef.current = execution;
      setIsExecutingApprovedAction(true);
      setApprovalContinuationError(null);
      const originUserID = currentUser.id;
      const originScope = workspaceScope;
      let originConversation = conversationRef.current;
      const originKnownMessages = new Map(knownMessagesRef.current);
      const isOriginActive = () => mountedRef.current
        && conversationRef.current.id === execution.conversationID
        && sessionIdentityRef.current.userID === originUserID
        && sessionIdentityRef.current.workspaceKey === originScope?.storageKey;
      const installMessages = (messages: Message[]) => {
        originConversation = {
          ...originConversation,
          updatedAt: new Date(),
          messages: messages.map((message) => toChatMessage(message, originKnownMessages, originConversation, selectedPersonality)),
        };
        if (isOriginActive()) {
          knownMessagesRef.current = new Map(originKnownMessages);
          conversationRef.current = originConversation;
          state.restoreMessages(messages);
          bumpConversation();
        }
      };
      try {
        toolRegistry.approve(approvalID, currentUser.id);
        const callID = `manual-${crypto.randomUUID()}`;
        const actionType = approvalStore.record(approvalID)?.actionType;
        const call = approvalExecutionCall(approvalID, callID, state.getSnapshot().messages);
        const decidedMessages = messagesWithApprovalDecision(state.getSnapshot().messages, approvalID, "approved");
        installMessages([...decidedMessages, call]);
        const result = await toolRegistry.execute({
          callId: callID,
          toolName: ToolName.executeApprovedAction,
          arguments: { approvalId: approvalID },
        }).catch((error: unknown) => ({
          callId: callID,
          toolName: ToolName.executeApprovedAction,
          content: JSON.stringify({
            ...JSON.parse(toolFailureEnvelope(ToolName.executeApprovedAction, error)),
            approvalId: approvalID,
            ...(actionType ? { actionType } : {}),
            outcomeUnknown: true,
          }),
          isError: true,
        }));
        const resultMessage = toolResultMessage(result.callId, result.toolName, result.content);
        installMessages([...decidedMessages, call, { ...resultMessage, timestamp: new Date(Math.max(Date.now(), call.timestamp.getTime() + 1)) }]);
        const recoveryKey = approvalRecoveryKey(originUserID, originScope, originConversation.id);
        unsavedApprovalConversationsRef.current.set(recoveryKey, originConversation);
        await syncService.flushPendingSaveAsync(originConversation, originUserID, originScope);
        if (unsavedApprovalConversationsRef.current.get(recoveryKey) === originConversation) {
          unsavedApprovalConversationsRef.current.delete(recoveryKey);
        }
        if (!isOriginActive() || execution.cancelled) return;

        if (aiUsageScope) {
          const gate = await aiUsageGate.refresh(aiUsageScope);
          if (!isOriginActive() || execution.cancelled) return;
          setUsageSummary(gate.summary);
          setUsageLimitBlockMessage(gate.blockMessage);
          if (gate.blockMessage) return;
        }
        endEnvironmentInteraction();
        const interaction = appEnvironmentContext?.beginInteraction("approvalContinuation");
        configureRequestContext(interaction);
        approvalHandoffRunRef.current = {
          existingMessageIDs: new Set(originConversation.messages.map((message) => message.id)),
        };
        state.startStreaming();
      } catch (error) {
        if (isOriginActive() && !execution.cancelled) {
          setApprovalContinuationError(`The approval could not finish reporting: ${describeError(error)}. Check the execution result before preparing another action.`);
          endEnvironmentInteraction();
        } else if (mountedRef.current && sessionIdentityRef.current.userID === originUserID && sessionIdentityRef.current.workspaceKey === originScope?.storageKey) {
          setApprovalContinuationError("An earlier approval result could not be saved. It is retained in this session; reopen that conversation to retry saving it.");
        }
      } finally {
        if (approvalExecutionRef.current === execution) approvalExecutionRef.current = null;
        if (mountedRef.current) setIsExecutingApprovedAction(false);
      }
    },
    [canDecideToolApproval, currentUser, toolRegistry, state, workspaceScope, selectedPersonality, bumpConversation, syncService, aiUsageScope, aiUsageGate, endEnvironmentInteraction, configureRequestContext, appEnvironmentContext, approvalStore],
  );

  const rejectToolAction = useCallback(
    (approvalID: string) => {
      if (!currentUser || !toolRegistry || !canDecideToolApproval(approvalID)) return;
      try {
        const record = toolRegistry.reject(approvalID, currentUser.id);
        const content = toolSuccessEnvelope(ToolName.executeApprovedAction, {
          approvalId: record.id,
          rejected: true,
          actionType: record.actionType,
        });
        const callID = `manual-${crypto.randomUUID()}`;
        const call = approvalExecutionCall(approvalID, callID, state.getSnapshot().messages);
        const result = toolResultMessage(callID, ToolName.executeApprovedAction, content);
        state.restoreMessages([
          ...messagesWithApprovalDecision(state.getSnapshot().messages, approvalID, "rejected"),
          call,
          { ...result, timestamp: new Date(call.timestamp.getTime() + 1) },
        ]);
      } catch (error) {
        console.warn("Tool rejection failed:", error);
      }
    },
    [canDecideToolApproval, currentUser, toolRegistry, state],
  );

  const approvalStatus = useCallback((approvalID: string) => approvalStore.record(approvalID)?.status, [approvalStore]);

  const setPersonalityID = useCallback((personalityID: string) => {
    if (pendingUserInputRequest) {
      setUserInputError("Answer or cancel the pending clarification before changing personalities.");
      return;
    }
    if (!availablePersonalities.some((personality) => personality.id === personalityID)) return;
    setSelectedPersonalityID(personalityID);
  }, [availablePersonalities, pendingUserInputRequest]);

  const setEffort = useCallback(
    (effort: SBotEffortLevel) => {
      if (pendingUserInputRequest) {
        setUserInputError("Answer or cancel the pending clarification before changing effort.");
        return;
      }
      setSelectedEffortState(effort);
      conversationRef.current = { ...conversationRef.current, selectedEffort: effort };
      bumpConversation();
    },
    [bumpConversation, pendingUserInputRequest],
  );

  const visibleRecents = new Map(recents.map((conversation) => [conversation.id, conversation]));
  if (userID) {
    const prefix = approvalRecoveryKey(userID, workspaceScope, "");
    for (const [key, retained] of unsavedApprovalConversationsRef.current) {
      if (key.startsWith(prefix)) visibleRecents.set(retained.id, retained);
    }
  }

  return {
    state,
    snapshot,
    conversation: conversationRef.current,
    recents: [...visibleRecents.values()].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()),
    legacyRecents,
    personalities: availablePersonalities,
    selectedPersonality,
    selectedEffort,
    isLoadingConversation,
    pendingUserInputRequest,
    userInputDraft,
    userInputError,
    isAwaitingUserInput,
    isSubmittingUserInput,
    isExecutingApprovedAction,
    approvalContinuationError,
    usageSummary,
    usageLimitBlockMessage,
    refreshUsageSummary,
    send,
    stop,
    steer,
    startNewChat,
    openConversation,
    deleteConversation,
    setPersonalityID,
    setEffort,
    updateUserInputDraft,
    submitPendingUserInput,
    cancelPendingUserInput,
    approveToolAction,
    rejectToolAction,
    canDecideToolApproval,
    approvalStatus,
  };
}

function approvalExecutionCall(approvalID: string, callID: string, messages: readonly Message[]): Message {
  return createMessage({
    role: "assistant",
    content: "",
    timestamp: new Date(Math.max(Date.now(), ...messages.map((message) => message.timestamp.getTime() + 1))),
    toolCalls: [{ id: callID, name: ToolName.executeApprovedAction, arguments: JSON.stringify({ approvalId: approvalID }) }],
  });
}

function approvalRecoveryKey(userID: string, scope: SBotChatWorkspaceScope | undefined, conversationID: string): string {
  return `${userID}/${scope?.storageKey ?? "personal"}/${conversationID}`;
}

function messagesWithApprovalDecision(messages: readonly Message[], approvalID: string, status: "approved" | "rejected"): Message[] {
  return messages.map((message) => {
    if (message.role !== "tool" || approvalCardFromContent(message.content)?.approvalID !== approvalID) return message;
    const envelope = JSON.parse(message.content);
    return { ...message, content: JSON.stringify({ ...envelope, approval: { ...envelope.approval, status } }) };
  });
}

/** Maps a provider message back to an SBotChatMessage, preserving known metadata. */
function toChatMessage(
  message: Message,
  known: Map<string, SBotChatMessage>,
  conversation: SBotChatConversation,
  personality: SBotAIPersonality,
): SBotChatMessage {
  const existing = known.get(message.id.toUpperCase()) ?? known.get(message.id);
  const isAssistant = message.role === "assistant";
  const candidate = providerMessageToChatMessage(message, {
    ...(isAssistant && {
      modelID: conversation.selectedModelID,
      modelName: message.modelName ?? conversation.selectedModelID,
      providerID: personality.providerID,
      personalityID: personality.id,
      personalityName: personality.name,
    }),
  });
  const chatMessage = existing
    ? {
        ...candidate,
        id: existing.id,
        createdAt: existing.createdAt,
        modelID: existing.modelID ?? candidate.modelID,
        modelName: existing.modelName ?? candidate.modelName,
        providerID: existing.providerID ?? candidate.providerID,
        personalityID: existing.personalityID ?? candidate.personalityID,
        personalityName: existing.personalityName ?? candidate.personalityName,
        approvalPresentationAfterMessageID: existing.approvalPresentationAfterMessageID,
      }
    : candidate;
  known.set(chatMessage.id, chatMessage);
  return chatMessage;
}

function completeApprovalHandoff(
  messages: SBotChatMessage[],
  existingMessageIDs: ReadonlySet<string>,
): SBotChatMessage[] {
  const approvalIndexes = messages.flatMap((message, index) => {
    const card = approvalCardFromContent(message.content);
    return !existingMessageIDs.has(message.id)
      && message.role === "tool"
      && card?.status === "pending"
      ? [index]
      : [];
  });
  const lastApprovalIndex = approvalIndexes.at(-1);
  if (lastApprovalIndex === undefined) return messages;

  const assistantIndex = lastIndexWhere(
    messages,
    (message) =>
      !existingMessageIDs.has(message.id)
      && message.role === "assistant"
      && message.toolCalls.length === 0
      && message.content.trim().length > 0,
  );
  if (assistantIndex <= lastApprovalIndex) return messages;
  const assistantMessageID = messages[assistantIndex]?.id;
  if (!assistantMessageID) return messages;

  const approvalIndexSet = new Set(approvalIndexes);
  return messages.map((message, index) =>
    approvalIndexSet.has(index)
      ? { ...message, approvalPresentationAfterMessageID: assistantMessageID }
      : message,
  );
}

function lastIndexWhere<T>(values: readonly T[], predicate: (value: T) => boolean): number {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== undefined && predicate(value)) return index;
  }
  return -1;
}

function pendingUserInputFromProviderMessages(
  messages: readonly Message[],
): { request: ChatToolUserInputRequest; nextToolRoundCount: number } | null {
  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message?.role !== "assistant") continue;
    const calls = message.toolCalls ?? [];

    for (let callIndex = calls.length - 1; callIndex >= 0; callIndex -= 1) {
      const call = calls[callIndex];
      if (!call || call.name !== ToolName.userClarification) continue;
      const hasMatchingLaterResult = messages.some(
        (candidate, candidateIndex) =>
          candidateIndex > messageIndex
          && candidate.role === "tool"
          && candidate.toolCallId === call.id
          && candidate.toolName === call.name,
      );
      if (hasMatchingLaterResult) continue;

      try {
        const argumentsValue = JSON.parse(call.arguments) as unknown;
        if (!argumentsValue || typeof argumentsValue !== "object" || Array.isArray(argumentsValue)) continue;
        const questionnaireJSON = (argumentsValue as Record<string, unknown>).questionnaireJson;
        if (typeof questionnaireJSON !== "string" || questionnaireJSON.trim().length === 0) continue;
        const request = chatToolUserInputRequestFromJSON(call.id, call.name, questionnaireJSON);

        let startIndex = 0;
        for (let index = messageIndex - 1; index >= 0; index -= 1) {
          if (messages[index]?.role === "user") {
            startIndex = index + 1;
            break;
          }
        }
        let toolRoundCount = 0;
        for (let index = startIndex; index <= messageIndex; index += 1) {
          const candidate = messages[index];
          if (candidate?.role === "assistant" && (candidate.toolCalls?.length ?? 0) > 0) {
            toolRoundCount += 1;
          }
        }
        return { request, nextToolRoundCount: Math.max(1, toolRoundCount) };
      } catch {
        continue;
      }
    }
  }
  return null;
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
